import { accessToken, runtimeConfig } from '@ck/auth'
import type { ChecklistStageResult, MediaAssetSummary, MediaPhase, Repair, RepairQuoteLine, RepairStatus, QuoteLineKind, ReportDetail, ReportListItem, ReportSummary, ReportVariant, TimelineEventSummary, WorkOrderDetail, WorkOrderSummary } from '@ck/domain'

export type { components, operations, paths } from './generated'

export interface ProblemDetails {
  type?: string
  title: string
  status: number
  detail?: string
  errors?: Record<string, string[]>
}

export interface PageResult<T> {
  items: T[]
  page: number
  pageSize: number
  total: number
}

export class ApiError extends Error {
  constructor(public readonly problem: ProblemDetails) { super(problem.detail ?? problem.title) }
}

// DTOs locais para a superfície autenticada do cliente. Eles isolam o Portal dos
// campos administrativos que ainda podem aparecer no payload até o OpenAPI ser
// sincronizado com os endpoints públicos.
export type CustomerReportLifecycle = 'queued' | 'ready' | 'failed'

export interface CustomerRepairDto {
  id: string
  workOrderId: string
  title: string
  description: string | null
  status: RepairStatus
  customerDecisionNote: string | null
  submittedForApprovalAt: string | null
  decidedAt: string | null
  version: number
  quoteLines: RepairQuoteLine[]
  total: number
  // Mantido apenas como chave de associação local com as etapas já públicas da OS;
  // o Portal nunca renderiza este identificador.
  stageExecutionId?: string | null
  stageCode?: string | null
  stageTitle?: string | null
}

export type CustomerReportSummaryDto = Omit<ReportSummary, 'variant' | 'status'> & {
  variant: ReportVariant | string
  status: string
}

export type CustomerReportApprovalDto = Pick<ReportDetail['approvals'][number], 'id' | 'documentSha256' | 'acceptedAt'>
export type CustomerReportDetailDto = Omit<ReportDetail, 'variant' | 'status' | 'approvals' | 'objectKey'> & {
  variant: ReportVariant | string
  status: string
  approvals: CustomerReportApprovalDto[]
}

export interface CustomerReportRequestDto {
  id: string
  revision: number
  variant: ReportVariant | string
  status: string
}

export interface CustomerWorkOrderFinancialsDto {
  parts: number
  labor: number
  approvedRepairs: number
  total: number
}

export type CustomerWorkOrderSummaryDto = WorkOrderSummary & { isPublished?: boolean }
export type CustomerWorkOrderDetailDto = WorkOrderDetail & {
  financials?: CustomerWorkOrderFinancialsDto | null
  partsTotal?: number | null
  laborTotal?: number | null
  approvedRepairsTotal?: number | null
  total?: number | null
}

export function isCustomerVisibleWorkOrder(workOrder: Pick<WorkOrderSummary, 'status'> & { isPublished?: boolean }): boolean {
  if (workOrder.isPublished === false) return false
  const status = workOrder.status.trim().toLowerCase()
  return status !== 'draft' && status !== 'cancelled' && status !== 'canceled'
}

export function isCustomerVisibleRepair(repair: Pick<Repair, 'status'>): boolean {
  return repair.status !== 'draft'
}

export function isCustomerVisibleTimeline(event: Pick<TimelineEventSummary, 'visibleToCustomer'>): boolean {
  return event.visibleToCustomer
}

export function isCustomerVisibleMedia(asset: Pick<MediaAssetSummary, 'visibleToCustomer'>): boolean {
  return asset.visibleToCustomer
}

export function isQuoteReport(report: Pick<ReportSummary, 'variant'>): boolean {
  return String(report.variant).trim().toLowerCase() === 'quote'
}

export function isCustomerVisibleReport(report: Pick<ReportSummary, 'variant' | 'status'>): boolean {
  const variant = String(report.variant).trim().toLowerCase()
  const status = String(report.status).trim().toLowerCase()
  return ['quote', 'summary', 'full'].includes(variant) && status !== 'draft'
}

export function normalizeReportLifecycle(status: string | null | undefined): CustomerReportLifecycle {
  switch (String(status ?? '').trim().toLowerCase()) {
    case 'ready':
    case 'generated':
    case 'completed':
      return 'ready'
    case 'failed':
    case 'error':
      return 'failed'
    default:
      return 'queued'
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await accessToken()
  const headers = new Headers(init.headers)
  if (!(init.body instanceof FormData)) headers.set('content-type', 'application/json')
  headers.set('accept', 'application/json')
  if (token) headers.set('authorization', `Bearer ${token}`)
  const response = await fetch(`${runtimeConfig.apiBaseUrl}${path}`, { ...init, headers })
  if (!response.ok) {
    const problem = await response.json().catch(() => ({ title: 'Falha na comunicação', status: response.status })) as ProblemDetails
    throw new ApiError(problem)
  }
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export interface CreateWorkOrderRequest { vehicleId: string; assignedTechnicianSubject?: string | null; expectedDelivery?: string | null }
export interface CreateWorkOrderResponse { id: string; number: string; version: number }

export const workOrdersApi = {
  list: (q = '') => api<PageResult<CustomerWorkOrderSummaryDto>>(`/work-orders?q=${encodeURIComponent(q)}`),
  get: (id: string) => api<CustomerWorkOrderDetailDto>(`/work-orders/${id}`),
  create: (payload: CreateWorkOrderRequest) =>
    api<CreateWorkOrderResponse>('/work-orders', { method: 'POST', body: JSON.stringify(payload) }),
  approveReport: (id: string) => api(`/reports/${id}/approval`, { method: 'POST', body: '{}' }),
  addPart: (workOrderId: string, payload: AddPartRequest) =>
    api<PartLineRecord>(`/work-orders/${workOrderId}/parts`, { method: 'POST', body: JSON.stringify(payload) }),
  addLabor: (workOrderId: string, payload: AddLaborRequest) =>
    api<LaborEntryRecord>(`/work-orders/${workOrderId}/labor`, { method: 'POST', body: JSON.stringify(payload) }),
  addTimelineEvent: (workOrderId: string, payload: AddTimelineRequest) =>
    api<TimelineEventRecord>(`/work-orders/${workOrderId}/timeline`, { method: 'POST', body: JSON.stringify(payload) }),
  publish: (workOrderId: string) => api<void>(`/work-orders/${workOrderId}/publish`, { method: 'POST' }),
  // Envia/substitui a foto de perfil do veículo (capa). O servidor comprime e garante uma capa
  // única por OS; a leitura é feita pelo coverMediaId do work-order (fonte única).
  uploadCover: (workOrderId: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return api<MediaUploadResult>(`/work-orders/${workOrderId}/cover`, { method: 'POST', body: form })
  },
  removeCover: (workOrderId: string) => api<void>(`/work-orders/${workOrderId}/cover`, { method: 'DELETE' })
}

export interface AddPartRequest { code: string; description: string; quantity: number; unitPrice: number }
export interface PartLineRecord { id: string; code: string; description: string; quantity: number; unitPrice: number; version: number }
export interface AddLaborRequest { description: string; hours: number; hourlyRate: number; technicianSubject?: string | null }
export interface LaborEntryRecord { id: string; description: string; hours: number; hourlyRate: number; technicianSubject: string | null; version: number }
export interface AddTimelineRequest { eventType: string; description: string; visibleToCustomer: boolean }
export interface TimelineEventRecord { id: string; eventType: string; description: string; actorSubject: string | null; visibleToCustomer: boolean; occurredAt: string; version: number }

export interface CreateCustomerRequest { name: string; email: string; phone?: string | null; cpfCnpj?: string | null; cep?: string | null }
export interface CustomerRecord { id: string; name: string; email: string; phone: string | null; cpfCnpj: string | null; cep: string | null }
export interface CustomerSummary { id: string; name: string; email: string; phone: string | null; cpfCnpj: string | null; cep: string | null }

export const customersApi = {
  create: (payload: CreateCustomerRequest) =>
    api<CustomerRecord>('/customers', { method: 'POST', body: JSON.stringify(payload) }),
  list: (q = '') => api<PageResult<CustomerSummary>>(`/customers?q=${encodeURIComponent(q)}`)
}

export interface CreateVehicleRequest {
  customerId: string
  brand: string
  model: string
  year: number
  plate: string
  vin?: string | null
  color?: string | null
}
export interface VehicleRecord {
  id: string
  customerId: string
  brand: string
  model: string
  year: number
  plate: string
  vin: string | null
  color: string | null
}

export const vehiclesApi = {
  create: (payload: CreateVehicleRequest) =>
    api<VehicleRecord>('/vehicles', { method: 'POST', body: JSON.stringify(payload) })
}

export const checklistsApi = {
  get: (workOrderId: string) => api<ChecklistStageResult[]>(`/work-orders/${workOrderId}/checklists`)
}

export const stagesApi = {
  complete: (workOrderId: string, stageId: string) =>
    api<void>(`/work-orders/${workOrderId}/stages/${stageId}/complete`, { method: 'POST' })
}

export interface QuoteLineInput { kind: QuoteLineKind; description: string; quantity: number; unitPrice: number }
export interface CreateRepairInput { title: string; description?: string | null; quoteLines: QuoteLineInput[] }
export interface UpdateRepairInput extends CreateRepairInput { expectedVersion: number }
export type RepairInput = CreateRepairInput
export type RepairMutationResult = { id: string; status: RepairStatus; version: number }

export const repairsApi = {
  listByWorkOrder: (workOrderId: string) => api<CustomerRepairDto[]>(`/work-orders/${workOrderId}/repairs`),
  create: (workOrderId: string, stageId: string, payload: CreateRepairInput) =>
    api<RepairMutationResult>(`/work-orders/${workOrderId}/stages/${stageId}/repairs`, { method: 'POST', body: JSON.stringify(payload) }),
  update: (id: string, payload: UpdateRepairInput) =>
    api<RepairMutationResult>(`/repairs/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  remove: (id: string) => api<void>(`/repairs/${id}`, { method: 'DELETE' }),
  submit: (id: string) => api<RepairMutationResult>(`/repairs/${id}/submit`, { method: 'POST' }),
  decide: (id: string, decision: 'approve' | 'reject', note?: string | null) =>
    api<RepairMutationResult>(`/repairs/${id}/decision`, { method: 'POST', body: JSON.stringify({ decision, note: note ?? null }) })
}

export interface MediaUploadResult { id: string; fileName: string; contentType: string; size: number; sha256: string; version: number }

export const mediaApi = {
  list: (workOrderId: string) => api<MediaAssetSummary[]>(`/work-orders/${workOrderId}/media`),
  upload: (payload: { file: File; workOrderId: string; checklistItemId?: string; visibleToCustomer?: boolean; phase?: MediaPhase }) => {
    const form = new FormData()
    form.append('file', payload.file)
    form.append('workOrderId', payload.workOrderId)
    if (payload.checklistItemId) form.append('checklistItemId', payload.checklistItemId)
    form.append('visibleToCustomer', String(payload.visibleToCustomer ?? true))
    form.append('phase', payload.phase ?? 'during')
    return api<MediaUploadResult>('/media', { method: 'POST', body: form })
  },
  async fetchFile(id: string): Promise<Blob> {
    const token = await accessToken()
    const headers = new Headers()
    if (token) headers.set('authorization', `Bearer ${token}`)
    const response = await fetch(`${runtimeConfig.apiBaseUrl}/media/${id}/file`, { headers })
    if (!response.ok) throw new ApiError({ title: 'Falha ao baixar mídia', status: response.status })
    return response.blob()
  },
  remove: (id: string) => api<void>(`/media/${id}`, { method: 'DELETE' })
}

export interface EmployeeSummary {
  id: string
  keycloakSubject: string
  displayName: string
  role: string
  active: boolean
  version: number
}

export interface RegisterAccountRequest { name: string; email: string; password: string; phone?: string | null; cpfCnpj?: string | null; cep?: string | null }
export interface RegisterAccountResponse { customerId: string; keycloakSubject: string }

export interface CreateMechanicRequest { displayName: string; email: string }
export interface CreateMechanicResponse {
  employeeId: string
  keycloakSubject: string
  displayName: string
  email: string
  temporaryPassword: string
}

export interface CreateCustomerAccountRequest { name: string; email: string; phone?: string | null; cpfCnpj?: string | null; cep?: string | null }
export interface CreateCustomerAccountResponse {
  customerId: string
  keycloakSubject: string
  name: string
  email: string
  temporaryPassword: string
}

export interface UpdateMyAccountRequest { name: string; phone: string | null }
export interface MyAccountResponse {
  name: string | null
  email: string | null
  phone: string | null
  roles: string[]
  kind: 'customer' | 'employee' | 'unknown'
}

export interface EmployeeRosterVehicle { workOrderId: string; vehicleBrand: string; vehicleModel: string; vehicleYear: number }
export interface EmployeeRosterEntry { id: string; displayName: string; role: string; active: boolean; memberSince: string; assignedVehicles: EmployeeRosterVehicle[] }

export const employeesApi = {
  list: () => api<EmployeeSummary[]>('/employees'),
  roster: () => api<EmployeeRosterEntry[]>('/employees/roster')
}

export const reportsApi = {
  listByWorkOrder: (workOrderId: string) => api<CustomerReportSummaryDto[]>(`/work-orders/${workOrderId}/reports`),
  // Listagem entre OS — apenas Manager/admin (ver Policies.Manager no backend).
  list: (params: { status?: string; page?: number; pageSize?: number } = {}) => {
    const search = new URLSearchParams()
    if (params.status) search.set('status', params.status)
    if (params.page) search.set('page', String(params.page))
    if (params.pageSize) search.set('pageSize', String(params.pageSize))
    return api<PageResult<ReportListItem>>(`/reports?${search.toString()}`)
  },
  get: (id: string) => api<CustomerReportDetailDto>(`/reports/${id}`),
  request: (workOrderId: string, variant: ReportVariant = 'summary') => api<CustomerReportRequestDto>(`/work-orders/${workOrderId}/reports?variant=${encodeURIComponent(variant)}`, { method: 'POST' }),
  requestQuote: (workOrderId: string) => api<CustomerReportRequestDto>(`/work-orders/${workOrderId}/reports?variant=quote`, { method: 'POST' }),
  async download(id: string): Promise<Blob> {
    const token = await accessToken()
    const headers = new Headers()
    if (token) headers.set('authorization', `Bearer ${token}`)
    const response = await fetch(`${runtimeConfig.apiBaseUrl}/reports/${id}/file`, { headers })
    if (!response.ok) throw new ApiError({ title: 'Falha ao baixar relatório', status: response.status })
    return response.blob()
  }
}

export interface DashboardSummary { vehiclesInShop: number; openWorkOrders: number; deliveriesThisMonth: number; rejectedItems: number }
export interface DashboardActivityItem { id: string; workOrderId: string; workOrderNumber: string; vehicleBrand: string; vehicleModel: string; eventType: string; description: string; actorSubject: string | null; occurredAt: string }

// Manager/admin apenas (ver Policies.Manager no backend) — consumido hoje só pelo Operacional,
// mantido nos 3 api-clients por paridade (AGENTS.md: pacotes @ck/* vendorizados sem sync automático).
export const dashboardApi = {
  summary: () => api<DashboardSummary>('/dashboard/summary'),
  activity: (take = 20) => api<DashboardActivityItem[]>(`/dashboard/activity?take=${take}`)
}

export const accountsApi = {
  register: (payload: RegisterAccountRequest) =>
    api<RegisterAccountResponse>('/accounts/register', { method: 'POST', body: JSON.stringify(payload) }),
  createMechanic: (payload: CreateMechanicRequest) =>
    api<CreateMechanicResponse>('/accounts/mechanics', { method: 'POST', body: JSON.stringify(payload) }),
  createCustomerAccount: (payload: CreateCustomerAccountRequest) =>
    api<CreateCustomerAccountResponse>('/accounts/customers', { method: 'POST', body: JSON.stringify(payload) }),
  me: () => api<MyAccountResponse>('/accounts/me'),
  updateMe: (payload: UpdateMyAccountRequest) =>
    api<MyAccountResponse>('/accounts/me', { method: 'PUT', body: JSON.stringify(payload) })
}
